import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loading, Empty } from "../components/common";
import { FramedAvatar, BadgePill, bgStyle, ItemVisual } from "../components/storeui";
import { Button } from "../components/ui/button";
import { Package, Check, RotateCcw, Store as StoreIcon, Sparkle } from "lucide-react";
import { toast } from "sonner";

const SLOTS = [["background", "Background"], ["animation", "Animation"], ["frame", "Frame"]];

export default function Collection() {
  const { user } = useAuth();
  const [inv, setInv] = useState(null);
  const [settings, setSettings] = useState({ background_item_id: null, animation_item_id: null, frame_item_id: null, badge_item_ids: [] });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/store/inventory").then(({ data }) => setInv(data)).catch(() => setInv({}));
    api.get("/store/customization").then(({ data }) => {
      const s = data.settings || {};
      setSettings({ background_item_id: s.background_item_id || null, animation_item_id: s.animation_item_id || null, frame_item_id: s.frame_item_id || null, badge_item_ids: s.badge_item_ids || [] });
    }).catch(() => {});
  }, []);

  const byId = useMemo(() => {
    const m = {};
    Object.values(inv || {}).flat().forEach((it) => { m[it.id] = it; });
    return m;
  }, [inv]);

  const eq = {
    background: byId[settings.background_item_id],
    animation: byId[settings.animation_item_id],
    frame: byId[settings.frame_item_id],
    badges: (settings.badge_item_ids || []).map((id) => byId[id]).filter(Boolean),
  };

  const toggleSlot = (kind, id) => {
    setDirty(true);
    setSettings((s) => ({ ...s, [`${kind}_item_id`]: s[`${kind}_item_id`] === id ? null : id }));
  };
  const toggleBadge = (id) => {
    setDirty(true);
    setSettings((s) => {
      const has = s.badge_item_ids.includes(id);
      if (has) return { ...s, badge_item_ids: s.badge_item_ids.filter((x) => x !== id) };
      if (s.badge_item_ids.length >= 3) { toast("You can display up to 3 badges"); return s; }
      return { ...s, badge_item_ids: [...s.badge_item_ids, id] };
    });
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/store/customization", settings); toast.success("Profile customization saved"); setDirty(false); }
    catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };
  const reset = () => { setSettings({ background_item_id: null, animation_item_id: null, frame_item_id: null, badge_item_ids: [] }); setDirty(true); };

  if (inv === null) return <Loading />;
  const empty = Object.values(inv).every((a) => !a.length);

  return (
    <div className="px-4 md:px-6 py-5 max-w-[1100px] mx-auto" data-testid="collection-page">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl md:text-3xl flex items-center gap-2"><Package className="text-gold" size={26} />My Collection</h1>
          <p className="text-sm text-muted-foreground mt-1">Equip your items — your public profile updates instantly.</p>
        </div>
        <Link to="/store" className="btn-blue rounded-full px-4 h-10 flex items-center gap-2 text-sm font-semibold" data-testid="collection-store-link"><StoreIcon size={16} />Visit Store</Link>
      </div>

      {empty ? (
        <Empty icon={Package} title="Your collection is empty" hint="Head to the VirtuStars Store to get animations, backgrounds, badges and frames." />
      ) : (
        <div className="grid lg:grid-cols-[360px_1fr] gap-6">
          {/* Live preview */}
          <div className="lg:sticky lg:top-20 h-fit">
            <div className="surface rounded-2xl overflow-hidden" data-testid="collection-preview">
              <div className="h-40 relative flex items-end justify-center" style={bgStyle(eq.background) || { background: "linear-gradient(180deg,#131826,#030303)" }}>
                <div className="absolute inset-0 bg-gradient-to-t from-[#030303]/70 to-transparent" />
                <div className="relative -mb-10 z-10"><FramedAvatar src={user?.avatar_url} name={user?.display_name} size={84} frame={eq.frame} animation={eq.animation} /></div>
              </div>
              <div className="pt-12 pb-5 px-4 text-center">
                <div className={`font-display font-bold text-xl ${eq.animation?.config?.target === "name" ? "vs-anim-shimmer text-gold" : ""}`}>{user?.display_name}</div>
                <div className="text-sm text-muted-foreground">@{user?.username}</div>
                {eq.badges.length > 0 && <div className="flex flex-wrap justify-center gap-1.5 mt-3">{eq.badges.map((b) => <BadgePill key={b.id} badge={b} />)}</div>}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={save} disabled={saving || !dirty} className="btn-blue rounded-full flex-1" data-testid="collection-save-btn">{saving ? "Saving…" : "Save Changes"}</Button>
              <Button onClick={reset} variant="outline" className="rounded-full border-white/15 bg-white/5" data-testid="collection-reset-btn"><RotateCcw size={15} className="mr-1" />Reset</Button>
            </div>
            <p className="text-[11px] text-muted-foreground text-center mt-2">Tip: tap an item to equip; tap again to remove. Up to 3 badges.</p>
          </div>

          {/* Inventory selectors */}
          <div className="space-y-7">
            {SLOTS.map(([kind, label]) => (
              <div key={kind}>
                <h3 className="font-display font-semibold mb-2 capitalize">{label}</h3>
                {(inv[kind] || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No {label.toLowerCase()} yet — <Link to="/store" className="text-eblue">get some in the Store</Link>.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {inv[kind].map((it) => {
                      const active = settings[`${kind}_item_id`] === it.id;
                      return (
                        <button key={it.id} onClick={() => toggleSlot(kind, it.id)} data-testid={`equip-${kind}-${it.id}`}
                          className={`surface rounded-xl p-2 text-left border-2 transition-colors ${active ? "border-eblue" : "border-transparent hover:border-white/15"}`}>
                          <ItemVisual item={it} size="h-20" />
                          <div className="flex items-center justify-between mt-1.5"><span className="text-xs font-medium truncate">{it.name}</span>{active && <Check size={14} className="text-eblue shrink-0" />}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
            {/* Badges */}
            <div>
              <h3 className="font-display font-semibold mb-2">Badges <span className="text-xs text-muted-foreground font-normal">({settings.badge_item_ids.length}/3)</span></h3>
              {(inv.badge || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No badges yet — <Link to="/store" className="text-eblue">collect some in the Store</Link>.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {inv.badge.map((b) => {
                    const active = settings.badge_item_ids.includes(b.id);
                    return (
                      <button key={b.id} onClick={() => toggleBadge(b.id)} data-testid={`equip-badge-${b.id}`}
                        className={`rounded-full border-2 transition-colors ${active ? "border-eblue" : "border-transparent"}`}>
                        <BadgePill badge={b} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
